import { useClipboard } from '@chakra-ui/react';
import { TextLink } from '@foam/buoy/src/components/TextLink/TextLink';
import { useCallback } from 'react';
import { useSnackbar } from 'ui-components';
import { Creator } from '~/clientApi/creator.model';
import { STMPredictionLabel } from '~/components/TalentCharts/STMPredictionLabel';
import { FEATURE_FLAGS } from '~/constants';
import { SupportedPlatforms } from '~/constants/platforms';
import { routes } from '~/core/router';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { usePlatformMetrics } from '~/hooks/usePlatformMetrics';
import { SITE_URL, humanizeDate } from '~/utils';

export function PlatformChartsCaption({
  creator,
  platform,
}: {
  creator?: Creator;
  platform: SupportedPlatforms;
}) {
  const { onCopy } = useClipboard(`${SITE_URL}${routes.connectionDashboard}`);
  const foamSmartTalentMetricsFeatureFlag = useFeature(
    FEATURE_FLAGS.foamSmartTalentMetrics,
  );
  const {
    isEmpty,
    isSTMPrediction,
    isPublicDemographicsVisible,
    connectionStatus,
    insights,
  } = usePlatformMetrics({
    creator,
    platform: platform as SupportedPlatforms,
  });
  const snackbar = useSnackbar();

  const onCopyTalentReconnectionLink = useCallback(() => {
    onCopy();
    snackbar.success({ description: 'Link copied to clipboard' });
  }, [onCopy, snackbar]);

  return (
    <>
      {!isEmpty && connectionStatus === 'connected' && !isSTMPrediction ? (
        <p className="text-12 font-400 text-neutral-300">
          Last updated {humanizeDate(insights?.data?.demographicsLastUpdatedAt)}
        </p>
      ) : null}

      {connectionStatus === 'disconnected' &&
      !foamSmartTalentMetricsFeatureFlag.isEnabled ? (
        <p className="text-12 font-400 text-neutral-300">
          Last updated {humanizeDate(insights?.data?.demographicsLastUpdatedAt)}
        </p>
      ) : null}

      {connectionStatus === 'disconnected' &&
      foamSmartTalentMetricsFeatureFlag.isEnabled &&
      !isSTMPrediction ? (
        <p className="text-12 font-400 text-neutral-300">
          Last updated {humanizeDate(insights?.data?.demographicsLastUpdatedAt)}
          .{' '}
          <TextLink
            size="body-md"
            className="font-300 text-link-primary-text-color-default"
            onClick={onCopyTalentReconnectionLink}
          >
            Copy talent reconnection link
          </TextLink>
        </p>
      ) : null}

      {isSTMPrediction && foamSmartTalentMetricsFeatureFlag.isEnabled ? (
        <STMPredictionLabel
          isSTMEnabled={isSTMPrediction}
          platform={platform as SupportedPlatforms}
        />
      ) : null}

      {/* TikTok's public demographics read like any other API metric (FOAM-23521). */}
      {isPublicDemographicsVisible && !isEmpty ? (
        <STMPredictionLabel isSTMEnabled={false} platform={platform} />
      ) : null}
    </>
  );
}
